import { useEffect } from 'react'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { AUTH_LOGOUT_EVENT } from '@/api/client'
import { useAuthStore } from '@/features/auth/authStore'
import { applyBrand } from '@/lib/brand'
import AppLayout from './components/AppLayout'
import Board from './pages/Board'
import Contacts from './pages/Contacts'
import Dashboard from './pages/Dashboard'
import Drip from './pages/Drip'
import SequenceEditorPage from './pages/SequenceEditorPage'
import Focus from './pages/Focus'
import Forms from './pages/Forms'
import FormEditorPage from './pages/FormEditorPage'
import Lists from './pages/Lists'
import MyProfile from './pages/MyProfile'
import ListDetailPage from './pages/ListDetailPage'
import Login from './pages/Login'
import Platform from './pages/Platform'
import Billing from './pages/Billing'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import PublicForm from './pages/PublicForm'
import Register from './pages/Register'
import Settings from './pages/Settings'
import Team from './pages/Team'
import Unsubscribe from './pages/Unsubscribe'
import Verify from './pages/Verify'
import Workflows from './pages/Workflows'
import WorkflowEditorPage from './pages/WorkflowEditorPage'

/** What a non-admin sees on an admin-only page (a bookmark or a shared link) instead of the page itself. */
function AdminOnly() {
  return (
    <div className="max-w-md mx-auto mt-24 px-6 text-center">
      <Lock size={28} className="mx-auto text-gray-300 mb-3" />
      <h1 className="text-lg font-semibold text-brand-darkest">Only admins can open this page</h1>
      <p className="text-sm text-gray-500 mt-1">Team members and workspace settings are managed by your agency’s admin. Ask them if something here needs changing.</p>
      <Link to="/" className="inline-block mt-4 px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark">
        Back to deals
      </Link>
    </div>
  )
}

export default function App() {
  const isAuth = useAuthStore((s) => s.isAuthenticated)
  const clearSession = useAuthStore((s) => s.clearSession)
  const agency = useAuthStore((s) => s.agency)
  const isPlatformAdmin = useAuthStore((s) => s.user?.role === 'PLATFORM_ADMIN')
  // Billing is a tenant feature — a PLATFORM_ADMIN has no agency context, so it's AGENCY_ADMIN only.
  const isAgencyAdmin = useAuthStore((s) => s.user?.role === 'AGENCY_ADMIN')
  const isAdmin = isAgencyAdmin || isPlatformAdmin

  // When a token refresh fails, the api client emits this event; drop the session.
  useEffect(() => {
    const handler = () => clearSession()
    window.addEventListener(AUTH_LOGOUT_EVENT, handler)
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler)
  }, [clearSession])

  // Apply the tenant's brand on load and whenever it changes.
  useEffect(() => {
    applyBrand(agency)
  }, [agency])

  return (
    <Routes>
      <Route path="/login" element={isAuth ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/register" element={isAuth ? <Navigate to="/" replace /> : <Register />} />
      <Route path="/verify" element={<Verify />} />
      <Route
        path="/forgot-password"
        element={isAuth ? <Navigate to="/" replace /> : <ForgotPassword />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/f/:id" element={<PublicForm />} />
      <Route element={isAuth ? <AppLayout /> : <Navigate to="/login" replace />}>
        <Route path="/" element={<Board />} />
        {/* Deep-linkable full view of a single deal (bookmark / "open in new tab"); the board
            behind opens the deal expanded and returns to "/" when it's closed. */}
        <Route path="/deals/:id" element={<Board />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/focus" element={<Focus />} />
        {/* "Needs attention" became Focus's Tasks tab — keep old links and bookmarks working. */}
        <Route path="/tasks" element={<Navigate to="/focus?tab=tasks" replace />} />
        <Route path="/workflows" element={<Workflows />} />
        <Route path="/workflows/new" element={<WorkflowEditorPage />} />
        <Route path="/workflows/:id" element={<WorkflowEditorPage />} />
        <Route path="/drip" element={<Drip />} />
        <Route path="/drip/new" element={<SequenceEditorPage />} />
        <Route path="/drip/:id" element={<SequenceEditorPage />} />
        <Route path="/forms" element={<Forms />} />
        <Route path="/forms/new" element={<FormEditorPage />} />
        <Route path="/forms/:id" element={<FormEditorPage />} />
        <Route path="/contacts" element={<Contacts />} />
        {/* Deep-linkable full view of a single contact — same pattern as /deals/:id. */}
        <Route path="/contacts/:id" element={<Contacts />} />
        <Route path="/lists" element={<Lists />} />
        <Route path="/lists/:id" element={<ListDetailPage />} />
        <Route path="/me" element={<MyProfile />} />
        <Route path="/team" element={isAdmin ? <Team /> : <AdminOnly />} />
        <Route path="/settings" element={isAdmin ? <Settings /> : <AdminOnly />} />
        <Route
          path="/billing"
          element={isAgencyAdmin ? <Billing /> : <Navigate to="/" replace />}
        />
        <Route
          path="/platform"
          element={isPlatformAdmin ? <Platform /> : <Navigate to="/" replace />}
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
